import React, { useState, useRef, useCallback } from 'react';
import { Box } from '@mui/material';

export default function Card3D({ front, back, width = 340, height = 480, sx = {}, flipped: controlledFlipped, onFlip }) {
  const [internalFlipped, setInternalFlipped] = useState(false);
  const flipped = controlledFlipped !== undefined ? controlledFlipped : internalFlipped;
  const [tilt, setTilt] = useState({ x: 0, y: 0 });
  const [glare, setGlare] = useState({ x: 50, y: 50, opacity: 0 });
  const cardRef = useRef(null);

  const handleMouseMove = useCallback((e) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left) / rect.width;
    const y = (e.clientY - rect.top) / rect.height;
    const tiltX = (y - 0.5) * -20;
    const tiltY = (x - 0.5) * 20;
    setTilt({ x: tiltX, y: tiltY });
    setGlare({ x: x * 100, y: y * 100, opacity: 0.07 });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setTilt({ x: 0, y: 0 });
    setGlare({ x: 50, y: 50, opacity: 0 });
  }, []);

  const handleClick = useCallback(() => {
    if (onFlip) { onFlip(); return; }
    if (back) setInternalFlipped((f) => !f);
  }, [back, onFlip]);

  const rx = tilt.x;
  const ry = flipped ? 180 + tilt.y : tilt.y;

  return (
    <Box
      sx={{
        width: { xs: Math.min(width, 300), sm: width },
        height: { xs: Math.min(height, 420), sm: height },
        perspective: '1200px',
        cursor: back || onFlip ? 'pointer' : 'default',
        ...sx,
      }}
    >
      <Box
        ref={cardRef}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
        sx={{
          width: '100%',
          height: '100%',
          position: 'relative',
          transformStyle: 'preserve-3d',
          transition: 'transform 0.8s cubic-bezier(0.16, 1, 0.3, 1)',
          transform: `rotateX(${rx}deg) rotateY(${ry}deg)`,
        }}
      >
        {/* Front face */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backfaceVisibility: 'hidden',
            WebkitBackfaceVisibility: 'hidden',
            borderRadius: '24px',
            overflow: 'hidden',
            transform: 'translateZ(0)',
          }}
        >
          {front}
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              borderRadius: '24px',
              pointerEvents: 'none',
              background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,${glare.opacity}), transparent 60%)`,
              transition: 'opacity 0.2s ease',
            }}
          />
        </Box>

        {/* Back face */}
        {back && (
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backfaceVisibility: 'hidden',
              WebkitBackfaceVisibility: 'hidden',
              transform: 'rotateY(180deg) translateZ(0)',
              borderRadius: '24px',
              overflow: 'hidden',
            }}
          >
            {back}
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                borderRadius: '24px',
                pointerEvents: 'none',
                background: `radial-gradient(circle at ${glare.x}% ${glare.y}%, rgba(255,255,255,${glare.opacity}), transparent 60%)`,
                transition: 'opacity 0.2s ease',
              }}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
}
